import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, CheckCircle2, XCircle, AlertTriangle, ScanLine, Loader2, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const READER_ID = 'eticket-qr-reader';

const RESULT_UI = {
  admit: { title: 'REDEEM', cls: 'bg-emerald-500', icon: CheckCircle2 },
  already_admitted: { title: 'ALREADY REDEEMED', cls: 'bg-amber-500', icon: AlertTriangle },
  unpaid: { title: 'NOT PAID', cls: 'bg-amber-600', icon: AlertTriangle },
  wrong_date: { title: 'WRONG DATE', cls: 'bg-amber-500', icon: AlertTriangle },
  cancelled: { title: 'CANCELLED', cls: 'bg-red-500', icon: XCircle },
  invalid: { title: 'INVALID TICKET', cls: 'bg-red-600', icon: XCircle },
};

// Force iOS Safari to render the camera feed inline (it otherwise tries to
// fullscreen the <video>, which shows up as a blank/white page).
const enforceInlineVideo = () => {
  const reader = document.getElementById(READER_ID);
  const video = reader && reader.querySelector('video');
  if (video) {
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('muted', 'true');
    video.muted = true;
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'cover';
  }
};

class ScannerErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (this.state.err) {
      return (
        <div className="bg-white/5 rounded-2xl p-6 border border-white/10 text-center" data-testid="scanner-error">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto" />
          <p className="text-white/80 text-sm mt-3">Camera scanner couldn't load. Please use manual entry below.</p>
          <button onClick={() => this.setState({ err: null })} className="mt-4 bg-cyan-500 text-[#0C2159] font-bold px-4 py-2 rounded-lg text-sm">Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function EticketScanner({ embedded = false }) {
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState('');
  const [camError, setCamError] = useState('');
  const qrRef = useRef(null);
  const busyRef = useRef(false);

  useEffect(() => () => { stopCamera(); }, []);

  const validate = async (token) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setLoading(true);
    await stopCamera();
    try {
      const { data } = await axios.post(`${API}/api/eticket/scan`, { qr_token: token });
      setResult(data);
    } catch (e) {
      setResult({ result: 'invalid', message: 'Scan failed. Try again.', ticket: null });
    } finally {
      setLoading(false);
      busyRef.current = false;
    }
  };

  const startCamera = async () => {
    setResult(null);
    setCamError('');
    setScanning(true);
    try {
      const qr = new Html5Qrcode(READER_ID, { verbose: false });
      qrRef.current = qr;
      await qr.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
        (decodedText) => validate(decodedText),
        () => {}
      );
      // iOS: video element only exists after start resolves.
      setTimeout(enforceInlineVideo, 50);
      setTimeout(enforceInlineVideo, 400);
    } catch (e) {
      await stopCamera();
      setCamError('Camera unavailable or permission denied. Use manual entry below.');
    }
  };

  const stopCamera = async () => {
    const qr = qrRef.current;
    if (qr) {
      try { if (qr.isScanning) await qr.stop(); } catch (e) {}
      try { await qr.clear(); } catch (e) {}
      qrRef.current = null;
    }
    setScanning(false);
  };

  const scanNext = async () => { setResult(null); setManual(''); await startCamera(); };

  const ui = result && (RESULT_UI[result.result] || RESULT_UI.invalid);
  const Icon = ui?.icon;

  return (
    <div className={embedded ? 'bg-[#0C2159] text-white rounded-2xl py-8 px-4' : 'min-h-screen bg-[#0C2159] text-white py-8 px-4'} data-testid="eticket-scanner-page">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2 justify-center mb-1">
          <ScanLine className="w-5 h-5 text-cyan-300" />
          <span className="text-xs font-semibold tracking-widest uppercase text-cyan-300">StayMii E-Ticket · Scanner</span>
        </div>
        <h1 className="text-2xl font-bold text-center mb-6">Scan to Admit</h1>

        {!result && (
          <ScannerErrorBoundary>
            <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
              {/* Stable container: html5-qrcode owns its children; React keeps
                  this div empty so the two never fight over the same DOM nodes. */}
              <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ aspectRatio: '1 / 1' }}>
                <div id={READER_ID} className="absolute inset-0 w-full h-full" />
                {!scanning && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <Camera className="w-12 h-12 text-white/30" />
                  </div>
                )}
              </div>

              {camError && <p className="text-amber-300 text-xs mt-3 text-center" data-testid="camera-error">{camError}</p>}

              {!scanning ? (
                <button onClick={startCamera} data-testid="start-scan-btn" className="w-full mt-4 bg-cyan-500 hover:bg-cyan-600 text-[#0C2159] font-bold py-3 rounded-xl flex items-center justify-center gap-2">
                  <Camera className="w-5 h-5" /> Start Camera Scan
                </button>
              ) : (
                <button onClick={stopCamera} data-testid="stop-scan-btn" className="w-full mt-4 bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl">Stop camera</button>
              )}

              <div className="mt-5 pt-4 border-t border-white/10">
                <p className="text-xs text-white/60 mb-2">Or enter the ticket code / link manually:</p>
                <div className="flex gap-2">
                  <input
                    data-testid="manual-token-input"
                    value={manual}
                    onChange={(e) => setManual(e.target.value)}
                    placeholder="Paste QR link or token"
                    className="flex-1 rounded-lg px-3 py-2 text-slate-900 text-sm outline-none"
                  />
                  <button onClick={() => manual && validate(manual)} disabled={!manual || loading} data-testid="manual-validate-btn" className="bg-cyan-500 hover:bg-cyan-600 text-[#0C2159] font-bold px-4 rounded-lg disabled:opacity-50">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
                  </button>
                </div>
              </div>
            </div>
          </ScannerErrorBoundary>
        )}

        {loading && !result && (
          <div className="flex justify-center mt-8"><Loader2 className="w-8 h-8 animate-spin text-cyan-300" /></div>
        )}

        {result && (
          <div className="bg-white rounded-2xl overflow-hidden shadow-2xl text-slate-900" data-testid="scan-result">
            <div className={`${ui.cls} p-6 text-white flex flex-col items-center`}>
              <Icon className="w-14 h-14" />
              <h2 className="text-2xl font-extrabold mt-2 tracking-wide" data-testid="scan-result-title">{ui.title}</h2>
              <p className="text-white/90 text-sm mt-1" data-testid="scan-result-message">{result.message}</p>
            </div>
            {result.ticket && (
              <div className="p-5 space-y-2 text-sm">
                <Row k="Attraction" v={result.ticket.company_name} />
                <Row k="Ticket" v={result.ticket.ticket_number} />
                <Row k="Guest" v={result.ticket.holder_name} />
                <Row k="Ticket type" v={result.ticket.rate_name || 'Admission'} />
                {result.ticket.visit_date && <Row k="Visit date" v={result.ticket.visit_date} />}
              </div>
            )}
            <div className="p-5 pt-0">
              <button onClick={scanNext} data-testid="scan-next-btn" className="w-full bg-[#0C2159] hover:bg-[#13317a] text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
                <RotateCcw className="w-4 h-4" /> Scan next ticket
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between border-b border-dashed border-slate-200 pb-1">
    <span className="text-slate-500">{k}</span><span className="font-semibold">{v}</span>
  </div>
);
